import React, { useEffect, useMemo, useRef, useState } from 'react';
import './PlaylistsPage.css';
import './SupportPage.css';

import mobileNoise from './bg_noise/mobile_noise.gif';
import tabletNoise from './bg_noise/tablet_noise.gif';
import desktopNoise from './bg_noise/desktop_noise.gif';
import './osci-logo-engine-v26-transparent';
import { LOGO_SEQUENCE, OSCI_SVG_ASSET_URLS, getInitialLogoVisualIndexForPage } from './logoVisuals';
import { DecodedArtImage, ResponsiveDecodedBackground } from './components/DecodedVisuals';

import instagramIcon from './soc icons/1_icons8-instagram.svg';
import spotifyProfileIcon from './soc icons/2_icons8-spotify.svg';
import youtubeSocialIcon from './soc icons/3_icons8-youtube.svg';
import pinterestIcon from './soc icons/5_icons8-pinterest.svg';
import homeIcon from './soc icons/0_home.svg';
import { applyRandomFlicker, decodeTextLocked, runAfterFontsReady } from './utils/textEffects';
import { handleSubmitMusicIntentClick } from './metaPixel';
import { useRegionalVisibility } from './regionalVisibility';
import PaymentMaintenanceModal from './components/PaymentMaintenanceModal';
import { PAYMENTS_TEMPORARILY_UNAVAILABLE } from './paymentMaintenance';

const DEFAULT_HERO_GIF = `${process.env.PUBLIC_URL}/static/img/Skull_scanline.gif`;

const SUPPORT_AREAS = [
  {
    label: 'Project growth',
  },
  {
    label: 'Playlist growth',
  },
  {
    label: 'Indie artist support',
  },
];

const SUPPORT_TIERS = [
  { amount: '$5', label: 'Signal', accent: '#00f6ff', accentRgb: '0, 246, 255', checkoutUrl: 'https://whop.com/checkout/plan_LN2oaqyBqxjGr' },
  { amount: '$15', label: 'Fresh Blood', accent: '#00dfff', accentRgb: '0, 223, 255', checkoutUrl: 'https://whop.com/checkout/plan_bJz4qHmwR1mRi' },
  { amount: '$30', label: 'Dark Fuel', accent: '#00c7ff', accentRgb: '0, 199, 255', checkoutUrl: 'https://whop.com/checkout/plan_GVZcv1gD3PUTk' },
  { amount: '$60', label: 'Night Pulse', accent: '#369bff', accentRgb: '54, 155, 255', checkoutUrl: 'https://whop.com/checkout/plan_t4Sc2HniYFrGM' },
  { amount: '$100', label: 'Obsidian Push', accent: '#6e76ff', accentRgb: '110, 118, 255', checkoutUrl: 'https://whop.com/checkout/plan_gDKL7myJUGKdj' },
  { amount: '$250', label: 'Heavy Signal', accent: '#9b5dff', accentRgb: '155, 93, 255', checkoutUrl: 'https://whop.com/checkout/plan_TNjrCrCrWLOw8' },
  { amount: '$500', label: 'Shadow Patron', accent: '#cf48ff', accentRgb: '207, 72, 255', checkoutUrl: 'https://whop.com/checkout/plan_Sjgt8iICnWDnI' },
  { amount: '$1000', label: 'Major Backer', accent: '#ff419f', accentRgb: '255, 65, 159', checkoutUrl: 'https://whop.com/checkout/plan_7RKIplut01o5X' },
  { amount: '$2500', label: 'Fund the Next Iva Gonzo Release', accent: '#ff376b', accentRgb: '255, 55, 107', checkoutUrl: 'https://whop.com/checkout/plan_n99SKoc8S8H3R' },
];

const FEATURED_SUPPORT_AMOUNTS = new Set(['$5', '$15', '$30']);
const FEATURED_SUPPORT_TIERS = SUPPORT_TIERS.filter((tier) => FEATURED_SUPPORT_AMOUNTS.has(tier.amount));
const EXPANDED_SUPPORT_TIERS = SUPPORT_TIERS.filter((tier) => !FEATURED_SUPPORT_AMOUNTS.has(tier.amount));

const SUPPORT_WAVE_TOTAL_MS = 1500;
const SUPPORT_WAVE_PAUSE_MS = 3000;
const SUPPORT_WAVE_PULSE_MS = 1200;

const SupportPage = () => {
  const { hideInstagram } = useRegionalVisibility();
  const [currentVisualIndex] = useState(() => getInitialLogoVisualIndexForPage('/support'));
  const osciCanvasRef = useRef(null);
  const osciAnimatorRef = useRef(null);
  const osciSvgListRef = useRef([]);
  const [osciReady, setOsciReady] = useState(false);
  const [waveActiveMap, setWaveActiveMap] = useState({});
  const [showAllSupportLevels, setShowAllSupportLevels] = useState(false);
  const waveTimersRef = useRef([]);
  const expandedTierTimersRef = useRef([]);
  const [visibleExpandedTierCount, setVisibleExpandedTierCount] = useState(0);
  const [showPaymentMaintenance, setShowPaymentMaintenance] = useState(false);
  const currentVisual = LOGO_SEQUENCE[currentVisualIndex] ?? LOGO_SEQUENCE[0];
  const visibleExpandedTiers = useMemo(
    () => EXPANDED_SUPPORT_TIERS.slice(0, visibleExpandedTierCount),
    [visibleExpandedTierCount]
  );
  const displayedTiers = useMemo(
    () => (showAllSupportLevels ? [...FEATURED_SUPPORT_TIERS, ...visibleExpandedTiers] : FEATURED_SUPPORT_TIERS),
    [showAllSupportLevels, visibleExpandedTiers]
  );

  useEffect(() => {
    const cleanups = [];
    let isMounted = true;

    const startEffects = () => {
      if (!isMounted) {
        return;
      }

      const decodeNodes = document.querySelectorAll('[data-decode]');
      const flickerNodes = document.querySelectorAll('.flicker-text');

      decodeNodes.forEach((node) => {
        const text = node.getAttribute('data-decode') || node.innerText;
        cleanups.push(decodeTextLocked(node, text));
      });

      flickerNodes.forEach((node) => {
        cleanups.push(applyRandomFlicker(node, 50, 200, 1, 0.7));
      });
    };

    const cancelFontWait = runAfterFontsReady(startEffects);

    return () => {
      isMounted = false;
      cancelFontWait();
      cleanups.forEach((cleanup) => cleanup && cleanup());
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadSvgAssets = async () => {
      try {
        const svgUrls = OSCI_SVG_ASSET_URLS;

        if (!svgUrls.length) {
          return;
        }

        const svgTexts = await Promise.all(svgUrls.map((url) => fetch(url).then((res) => res.text())));
        if (isMounted) {
          osciSvgListRef.current = svgTexts;
          setOsciReady(true);
        }
      } catch (error) {
        console.error('Failed to load osci SVG assets for support page', error);
      }
    };

    loadSvgAssets();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const visual = LOGO_SEQUENCE[currentVisualIndex];
    if (visual.type !== 'osci') {
      if (osciAnimatorRef.current) {
        osciAnimatorRef.current.destroy();
        osciAnimatorRef.current = null;
      }
      return undefined;
    }
    if (!osciReady || !window.OsciLogoAnimator) {
      return undefined;
    }

    const canvas = osciCanvasRef.current;
    if (!canvas) {
      return undefined;
    }

    const animator = new window.OsciLogoAnimator({
      canvas,
      svgList: osciSvgListRef.current.length ? osciSvgListRef.current : undefined,
      svgIndex: 0,
      cycleOnRelock: true,
      direction: -1,
      beams: 3,
      transparent: true,
      trailFade: 0.1,
      background: 'rgba(0,0,0,0)',
      beamColor: '#ff0000',
      baseOutlineColor: '#0011ff',
      scan: false,
      vig: 0,
      rgbAberration: 20,
      speed: 1.55,
      speedBoost: 0.5,
      intensity: 2.2,
      highlightWidthPx: 60,
      maxFps: 60,
      renderScale: 0.99,
      quality: 1.0,
      adaptive: false,
      traceStepPx: 5,
      strokePx: 0.5,
      glowPx: 0,
      miterLimit: 14,
      baseOutlineAlpha: 0.1,
      baseOutlineStrokePx: 0.1,
      frameJitter: 0,
      stableJitterPx: 0,
      lostJitterPx: 0,
      dropout: 0.3,
      signalEnabled: true,
      signalNoise: 1,
      signalHoldMin: 3.2,
      signalHoldMax: 4.4,
      signalDropMin: 0.77,
      signalDropMax: 2.28,
      glitch: true,
      glitchChance: 0.37,
    });

    osciAnimatorRef.current = animator;
    animator.start();

    return () => {
      animator.destroy();
      osciAnimatorRef.current = null;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };
  }, [currentVisualIndex, osciReady]);

  useEffect(() => {
    expandedTierTimersRef.current.forEach((timerId) => clearTimeout(timerId));
    expandedTierTimersRef.current = [];

    if (!showAllSupportLevels) {
      setVisibleExpandedTierCount(0);
      return undefined;
    }

    setVisibleExpandedTierCount(1);

    EXPANDED_SUPPORT_TIERS.slice(1).forEach((_, index) => {
      const timerId = setTimeout(() => {
        setVisibleExpandedTierCount(index + 2);
      }, (index + 1) * 90);
      expandedTierTimersRef.current.push(timerId);
    });

    return () => {
      expandedTierTimersRef.current.forEach((timerId) => clearTimeout(timerId));
      expandedTierTimersRef.current = [];
    };
  }, [showAllSupportLevels]);

  useEffect(() => {
    const clearWaveTimers = () => {
      waveTimersRef.current.forEach((timerId) => clearTimeout(timerId));
      waveTimersRef.current = [];
    };

    const scheduleWaveCycle = () => {
      const waveStepMs = displayedTiers.length
        ? SUPPORT_WAVE_TOTAL_MS / displayedTiers.length
        : SUPPORT_WAVE_TOTAL_MS;

      displayedTiers.forEach((tier, tierIndex) => {
        const activateId = setTimeout(() => {
          setWaveActiveMap((prev) => ({ ...prev, [tier.amount]: true }));

          const deactivateId = setTimeout(() => {
            setWaveActiveMap((prev) => {
              if (!prev[tier.amount]) {
                return prev;
              }

              const next = { ...prev };
              delete next[tier.amount];
              return next;
            });
          }, SUPPORT_WAVE_PULSE_MS);

          waveTimersRef.current.push(deactivateId);
        }, Math.round(tierIndex * waveStepMs));

        waveTimersRef.current.push(activateId);
      });

      const repeatId = setTimeout(
        scheduleWaveCycle,
        SUPPORT_WAVE_TOTAL_MS + SUPPORT_WAVE_PAUSE_MS,
      );
      waveTimersRef.current.push(repeatId);
    };

    scheduleWaveCycle();

    return () => {
      clearWaveTimers();
      setWaveActiveMap({});
    };
  }, [displayedTiers]);

  const renderTierCard = (tier, options = {}) => (
    <article
      key={tier.amount}
      className={`support-tier-card ${options.className || ''}`.trim()}
      style={{
        '--tier-accent': tier.accent,
        '--tier-accent-rgb': tier.accentRgb,
      }}
    >
      <a
        href={PAYMENTS_TEMPORARILY_UNAVAILABLE ? '#payment-maintenance' : tier.checkoutUrl}
        className={`support-tier-card__button ${waveActiveMap[tier.amount] ? 'wave-active' : ''}`}
        aria-label={`${tier.label} ${tier.amount}`}
        aria-haspopup={PAYMENTS_TEMPORARILY_UNAVAILABLE ? 'dialog' : undefined}
        onClick={(event) => {
          if (!PAYMENTS_TEMPORARILY_UNAVAILABLE) return;
          event.preventDefault();
          setShowPaymentMaintenance(true);
        }}
      >
        <span className="btn-glass" aria-hidden="true"></span>
        <span className="btn-label">
          <span className="btn-name">{tier.label}</span>
          <span className="btn-price">{tier.amount}</span>
        </span>
      </a>
    </article>
  );

  return (
    <div className="App playlists-page support-page">
      <PaymentMaintenanceModal
        open={showPaymentMaintenance}
        onClose={() => setShowPaymentMaintenance(false)}
        source="support"
      />

      <div id="background">
        <ResponsiveDecodedBackground mobileSrc={mobileNoise} tabletSrc={tabletNoise} desktopSrc={desktopNoise} />
        <img src={DEFAULT_HERO_GIF} alt="" aria-hidden="true" id="animated-gif" />
      </div>

      <div id="content-wrapper" className="playlists-wrapper">
        <div className="playlists-panel support-panel">
          <a href="/" className="logo-home-link" aria-label="Back to home">
            {currentVisual.type === 'image' ? (
              <DecodedArtImage
                src={currentVisual.src}
                alt="IVA Gonzo logo"
                id="background-gif"
                aspectRatio={currentVisual.aspectRatio}
              />
            ) : (
              <div id="background-gif" className="osci-wrap">
                {osciReady ? (
                  <canvas id="osci-support" ref={osciCanvasRef} />
                ) : null}
              </div>
            )}
          </a>

          <header className="playlists-header support-header">
            <p className="header-eyebrow">
              BACK THE WORK BEHIND{' '}
              {hideInstagram ? (
                <span>IVA GONZO</span>
              ) : (
                <a
                  href="https://www.instagram.com/ivagonzo/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  IVA GONZO
                </a>
              )}
            </p>
            <h1 className="page-title flicker-text no-fade" data-decode="SUPPORT THE WORK">
              SUPPORT THE WORK
            </h1>
            <p className="page-subtitle">PLAYLISTS • MUSIC • INDIE ARTISTS</p>
            <p className="page-description">
              If you genuinely like what I do, this is a direct way to support the project behind it. Your support
              helps me push indie artists further, grow the playlists, and spread the dark alternative aesthetic I
              believe in through more music, playlists, and social content. Every bit of support helps me keep
              building, and it means a lot to know the work is genuinely valuable to someone.
            </p>
          </header>

          <section className="support-section">
            <div className="support-section__intro">
              <span className="support-section__eyebrow">Support levels</span>
              <h2>Choose the amount that feels right.</h2>
            </div>
            <div className="support-tiers-shell">
              <div className="support-tiers-grid support-tiers-grid--featured">
                {FEATURED_SUPPORT_TIERS.map((tier) => renderTierCard(tier))}
              </div>
              <button
                type="button"
                className="support-tiers-toggle"
                onClick={() => setShowAllSupportLevels((prev) => !prev)}
                aria-expanded={showAllSupportLevels}
              >
                <span className="btn-glass" aria-hidden="true"></span>
                <span className="support-tiers-toggle__label">
                  {showAllSupportLevels ? 'SHOW FEWER LEVELS' : 'SHOW ALL SUPPORT LEVELS'}
                </span>
                <span className="support-tiers-toggle__icon" aria-hidden="true">
                  {showAllSupportLevels ? '−' : '+'}
                </span>
              </button>
              {showAllSupportLevels && (
                <div className="support-tiers-grid support-tiers-grid--expanded">
                  {visibleExpandedTiers.map((tier) =>
                    renderTierCard(tier, {
                      className: 'support-tier-card--reveal-in',
                    })
                  )}
                </div>
              )}
            </div>
            <p className="support-share-note">
              Support does not have to be financial. Posting one of my{' '}
              <a href="/playlists" className="support-share-note__link">
                playlist links
              </a>{' '}
              on your socials also helps the project grow, especially when you add a few personal words about the
              music, the vibe, or the curation.
            </p>
          </section>

          <section className="support-section">
            <div className="support-section__intro support-section__intro--compact">
              <span className="support-section__eyebrow">Where your support goes</span>
            </div>
            <div className="support-funding-strip">
              {SUPPORT_AREAS.map((item) => (
                <div key={item.label} className="support-funding-strip__item">
                  {item.label}
                </div>
              ))}
            </div>
          </section>

          <section className="support-section support-submit-cta-section">
            <article className="support-submit-cta">
              <span className="support-submit-cta__eyebrow">Submission info</span>
              <p className="support-submit-cta__copy">
                Support is not paid placement. Donations do not buy playlist placement, faster review, or better odds.
              </p>
              <p className="support-submit-cta__copy support-submit-cta__copy--secondary">
                If you want to submit music to an independent Spotify playlist curator for review and possible playlist consideration, visit the{' '}
                <a
                  className="support-submit-cta__inline-link"
                  href="/submit-your-music"
                  onClick={(event) =>
                    handleSubmitMusicIntentClick(event, {
                      source_page: 'support',
                      cta_location: 'submission_info_inline_link',
                    })
                  }
                >
                  Submit Your Music
                </a>{' '}
                page. Every track goes through the normal review process, and approval is never guaranteed.
              </p>
              <div className="support-submit-cta__actions">
                <a
                  href="/submit-your-music"
                  className="support-submit-cta__link"
                  onClick={(event) =>
                    handleSubmitMusicIntentClick(event, {
                      source_page: 'support',
                      cta_location: 'submission_info_button',
                    })
                  }
                >
                  <span className="btn-glass" aria-hidden="true"></span>
                  <span className="support-submit-cta__label">SUBMIT YOUR MUSIC</span>
                </a>
              </div>
            </article>
          </section>

        </div>

        <footer id="social-icons">
          <a href="/" aria-label="Back to home">
            <img className="social-icon" src={homeIcon} alt="Home" />
          </a>
          {!hideInstagram && (
            <a href="https://www.instagram.com/ivagonzo/" target="_blank" rel="noopener noreferrer">
              <img className="social-icon" src={instagramIcon} alt="Instagram" />
            </a>
          )}
          <a
            href="https://open.spotify.com/user/11185646448?si=e1a94395b1054a48"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img className="social-icon" src={spotifyProfileIcon} alt="Spotify" />
          </a>
          <a href="https://youtube.com/@ivagonzo/" target="_blank" rel="noopener noreferrer">
            <img className="social-icon" src={youtubeSocialIcon} alt="YouTube" />
          </a>
          <a href="https://www.pinterest.com/ivagonzo/" target="_blank" rel="noopener noreferrer">
            <img className="social-icon" src={pinterestIcon} alt="Pinterest" />
          </a>
        </footer>
      </div>
    </div>
  );
};

export default SupportPage;
